import { useTrackingUuid } from '@sortlist-frontend/tracking';
import Router from 'next/router';
import { useEffect, useRef } from 'react';

import { sendWebVitalsReport } from '../sendWebVitalsReport';

interface RouteChangeOptions {
  shallow?: boolean;
}

export const useLogNavigationTime = () => {
  const routeChangeStartedAt = useRef<number | null>(null);
  const { sessionUuid, userUuid } = useTrackingUuid();
  const trackingIds = useRef({ sessionUuid, userUuid });
  trackingIds.current = { sessionUuid, userUuid };

  useEffect(() => {
    const handleRouteChangeStart = (_url: string, options?: RouteChangeOptions) => {
      routeChangeStartedAt.current = options?.shallow === true ? null : performance.now();
    };

    const handleRouteChangeComplete = (url: string) => {
      const startedAt = routeChangeStartedAt.current;
      routeChangeStartedAt.current = null;
      if (startedAt == null) return;

      void sendWebVitalsReport({
        kind: 'navigation',
        route: Router.pathname,
        path: new URL(url, window.location.origin).pathname,
        ...trackingIds.current,
        durationMs: Math.round(performance.now() - startedAt),
      });
    };

    const handleRouteChangeError = () => {
      routeChangeStartedAt.current = null;
    };

    Router.events.on('routeChangeStart', handleRouteChangeStart);
    Router.events.on('routeChangeComplete', handleRouteChangeComplete);
    Router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      Router.events.off('routeChangeStart', handleRouteChangeStart);
      Router.events.off('routeChangeComplete', handleRouteChangeComplete);
      Router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, []);
};
